import { Formik } from "formik";
import moment from "moment";
import { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { AppContext } from "../appContext/AppContext";
import { KEY_LOCALSTORAGE } from "../auth/core/_consts";
import ComponentPrint from "../component/templateComponent/ComponentPrint";
import { phieuTiepNhan } from "../component/templateComponent/conponents/PhieuTiepNhan";
import { convertDataTiepNhan } from "../component/templateComponent/conponents/Utils";
import { CODE, KEY_DS_DANH_MUC_TIEP_DON, KEY_DS_TAB_TIEP_NHAN, REGEX } from "../utils/Constant";
import { formatDateDTO, validateNgay } from "../utils/FormatUtils";
import { getIndexedDBItem } from "../utils/IndexedDB";
import { localStorageItem } from "../utils/LocalStorage";
import { daySchema, monthSchema, yearSchema } from "../utils/ValidationSchemaUtils";
import { CODE_DOI_TUONG, initialValuesTiepDon } from "./constants/PhanHeTiepNhan";
import { IDanhSachDanhMuc, ITiepDon } from "./models/TiepDonModel";
import './PhanHeTiepNhan.scss';
import { addBenhNhan, updateBenhNhan } from "./services/TiepNhanServices";
import TiepNhan from "./tab-tiep-nhan/TiepNhan";
import { checkInsuranceInfoChange, convertDataBenhNhan } from "./util/FunctionUtil";
import {toBoolean} from "../utils/AppFunction";

// Validation schema factory - tạo 1 lần, tái sử dụng
const createValidationSchema = (intl: any) => Yup.object({
    patient: Yup.object({
        person: Yup.object({
            fullName: Yup.string()
                .required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' }))
                .nullable()
                .matches(REGEX.TEN, intl.formatMessage({ id: 'VALIDATE.TEN' }))
                .max(100, 'Độ dài tối đa 100 ký tự!'),
            dobDay: Yup.number()
                .typeError('Vui lòng nhập số')
                .nullable()
                .min(1, 'Ngày không hợp lệ')
                .max(31, 'Ngày không hợp lệ')
                .test('is-valid-date', 'Ngày không hợp lệ', function (value) {
                    const { dobDay, dobMonth, dobYear } = this.parent;
                    if (!dobMonth || (dobMonth && dobYear)) return true;
                    return validateNgay(dobDay, dobMonth, dobYear);
                }),
            dobMonth: Yup.number()
                .typeError('Vui lòng nhập số')
                .nullable()
                .min(1, 'Tháng không hợp lệ')
                .max(12, 'Tháng không hợp lệ')
                .test('is-valid-month', 'Ngày không hợp lệ', function (value) {
                    const { dobYear } = this.parent;
                    if (!dobYear) return true;
                    return true;
                }),
            dobYear: Yup.number()
                .typeError('Vui lòng nhập số')
                .required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' }))
                .nullable()
                .min(1900, 'Năm lớn hơn hoặc bằng 1900')
                .max(new Date().getFullYear(), 'Năm nhỏ hơn hoặc bằng hiện tại')
                .test(
                    'is-valid-year',
                    'Ngày sinh không được lớn hơn ngày hiện tại',
                    function (value, context) {
                        const { dobDay, dobMonth, dobYear } = context.parent;

                        if (!dobMonth) return true;

                        if (dobDay) {
                            const inputDate = new Date(dobYear, dobMonth - 1, dobDay);
                            const today = new Date();
                            return inputDate <= today;
                        }

                        const today = new Date();
                        if (
                            dobYear > today.getFullYear() ||
                            (dobYear === today.getFullYear() &&
                                dobMonth &&
                                dobMonth > today.getMonth() + 1)
                        ) {
                            return false;
                        }
                        return true;
                    }
                )
                .test(
                    'is-valid-dob',
                    'Ngày sinh không hợp lệ',
                    function (value, context) {
                        const { dobDay, dobMonth, dobYear } = context.parent;

                        return moment([dobYear, (dobMonth ?? 1) - 1, dobDay ?? 1]).isValid();
                    }
                ),
            idNumber: Yup.string()
                .nullable()
                .test(
                    'valid-idNumber',
                    'Số CCCD hoặc Hộ chiếu không hợp lệ',
                    (value: any) =>
                        /^[0-9]{9}$/.test(value) ||
                        /^[0-9]{12}$/.test(value) ||
                        /^[A-Z][0-9]{7}$/.test(value) ||
                        !value
                ),
            phoneNumber: Yup.string()
                .matches(/^\d+$/, {
                    message: 'Số điện thoại chỉ chứa chữ số',
                    excludeEmptyString: true,
                })
                .length(10, 'Số điện thoại có độ dài 10 ký tự'),
        }),
    }),
    visit: Yup.object({
        visitObjectType: Yup.object()
            .required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' }))
            .nullable(),
        dept: Yup.object().nullable().when('service', {
            is: (service: any) => service,
            then: Yup.object().nullable().required(intl.formatMessage({ id: 'VALIDATION.REQUIRE' })),
        })
    }),
    insurance: Yup.object().shape({
        insCodeDoiTuong: Yup.string().nullable().length(2, 'Mã đối tượng gồm 2 ký tự'),
        insCodeMucHuong: Yup.string().nullable().length(1, 'Mã mức hưởng gồm 1 ký tự'),
        insCodeNoiPhatHanh: Yup.string().nullable().length(2, 'Mã nơi phát hành gồm 2 ký tự'),
        insCodeMaBhxh: Yup.string()
            .nullable()
            .matches(/^\d+$/, {
                message: 'Mã BHXH chỉ chứa chữ số',
                excludeEmptyString: true,
            })
            .length(10, 'Mã BHXH gồm 10 ký tự'),

        insFromDay: daySchema,
        insFromMonth: monthSchema,
        insFromYear: yearSchema,

        insToDay: daySchema,
        insToMonth: monthSchema,
        insToYear: yearSchema,

        effectiveFromDay: daySchema,
        effectiveFromMonth: monthSchema,
        effectiveFromYear: yearSchema,

        effectiveToDay: daySchema,
        effectiveToMonth: monthSchema,
        effectiveToYear: yearSchema,

        insNgaymienchitraDay: daySchema,
        insNgaymienchitraMonth: monthSchema,
        insNgaymienchitraYear: yearSchema,

        insNgaydu5namDay: daySchema,
        insNgaydu5namMonth: monthSchema,
        insNgaydu5namYear: yearSchema,
    }),
});

interface PhanHeTiepNhanContextProps {
    DSDichVu: any[];
    thongTinBenhNhan: {
        isChangeThongTinBenhNhan?: boolean
        isExamined?: boolean
        isMedicalExamSvcPaid?: boolean
    };
    setThongTinBenhNhan: (value: any) => void;
    handleAddTab: (eventKey: string) => void;
    setDSDichVu: Dispatch<SetStateAction<any[]>>;
    isPrint?: boolean;
    setIsPrint?: (isPrint: boolean) => void;
    isPay: { boolean: boolean, page: number, pageSize: number};
    setIsPay: (isPay: { boolean: boolean, page: number, pageSize: number; }) => void;
    orderMain: object | null;
    setOrderMain: Dispatch<SetStateAction<any>>;
    convertDataBenhNhan: (data: any) => ITiepDon
  setPatientRenewToggle: Dispatch<SetStateAction<any>>;
  patientRenewToggle: boolean;
  handleNewPatient?: () => void;
}

const initialContext = {
    thongTinBenhNhan: {} ,
    setThongTinBenhNhan: () => { },
    handleAddTab: () => { },
    setDSDichVu: () => { },
    DSDichVu: [],
    isPrint: false,
    isPay: {boolean: false, page: 0, pageSize: 0},
    setIsPay: () => { },
    orderMain: null,
    setOrderMain: () => { },
    convertDataBenhNhan: () => ({} as ITiepDon),
  setPatientRenewToggle: () => { },
  patientRenewToggle: false,
  handleNewPatient: () => { },
}

export const PhanHeTiepNhanContext = createContext<PhanHeTiepNhanContextProps>(initialContext)

export const usePhanHeTiepNhanContext = () => useContext(PhanHeTiepNhanContext);

export const PhanHeTiepNhanContextProvider = (props: { children: React.ReactNode, value: any }) => {
  const {children, value = {}} = props;
  const contextValue = usePhanHeTiepNhanContext();
  return <PhanHeTiepNhanContext.Provider value={{...contextValue, ...value}}>
    {children}
  </PhanHeTiepNhanContext.Provider>
}

export const PhanHeTiepNhan: FC = () => {
    const { setEventKey, setIsLoading } = useContext(AppContext);
    let [isPrint, setIsPrint] = useState<boolean>(false);
    let [isPay, setIsPay] = useState<{ boolean: boolean, page: number, pageSize: number}>( { boolean: false, page: 0, pageSize: 0});
    const [thongTinBenhNhan, setThongTinBenhNhan] = useState<any>(initialValuesTiepDon);
    const [DSDichVu, setDSDichVu] = useState<any[]>([]);
    const [openDSBenhNhan, setOpenDSDSBenhNhan] = useState(false);
    const [orderMain, setOrderMain] = useState<object | null>(null);
    const [dsDanhMuc, setDsDanhMuc] = useState<IDanhSachDanhMuc>();
  const [patientRenewToggle, setPatientRenewToggle] = useState<boolean>(false);
    const currentUser = localStorageItem.get(KEY_LOCALSTORAGE.CURRENT_USER);
    const configuration = localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION);
    const formRef = useRef<any>(null);

    const intl = useIntl();

    useEffect(() => {
        handleGetDsDanhMuc();
    }, []);

    const handleGetDsDanhMuc = async () => {
        try {
            const responseDsDanhmuc: IDanhSachDanhMuc = await getIndexedDBItem(
                KEY_DS_DANH_MUC_TIEP_DON
            );
            if (responseDsDanhmuc) {
                setDsDanhMuc(responseDsDanhmuc);
            }

        } catch (error) {
            console.error(error);
        }
    };

    const handleAddTab = useCallback((eventKey: string) => {
        let data = localStorageItem.get(KEY_DS_TAB_TIEP_NHAN) ? localStorageItem.get(KEY_DS_TAB_TIEP_NHAN) : [];
        if (!data.includes(eventKey)) {
            data.push(eventKey);
        }
        data.sort((a: string, b: string) => a > b ? 1 : -1);
        localStorageItem.set(KEY_DS_TAB_TIEP_NHAN, data)
        setEventKey(eventKey)
    }, [setEventKey]);

    // Sử dụng useMemo để tránh tạo schema mới mỗi lần render
    const validationSchema = useMemo(() => createValidationSchema(intl), [intl]);

    const handleFormSubmit = async (values: ITiepDon) => {
        const currentDate = new Date();
        let patientValues = values?.patient;
        let visitValues = values?.visit;
        let ordersValues = values?.orders?.map((item: any) => {
            return {
                ...item,
                termId: item?.termId || item?.id,
                id: item?.termId ? item?.id : null
            };
        }) || [];
        let personValues = patientValues?.person;
        let insuranceValues = values?.insurance;
        let ordersKb = orderMain && typeof orderMain ==='object'
            ? {
            ...orderMain,
            termId: visitValues?.service?.id,
            fulfillDeptId: visitValues?.dept?.id || "",
            fulfillDeptName: visitValues?.dept?.name || "",
        } : {
            termId: visitValues?.service?.id,
            fulfillDeptId: visitValues?.dept?.id || "",
            fulfillDeptName: visitValues?.dept?.name || "",
        }
        let arr_orders = visitValues?.service?.id ? ordersValues.concat([ordersKb]) : ordersValues;

        let isBHYT = CODE_DOI_TUONG.BAO_HIEM === visitValues?.visitObjectType?.code
        let userInfo = {
            indicationPerson: currentUser?.name,
            requestDeptName: currentUser?.departments?.[0]?.name || null
        };
        let districtValue = dsDanhMuc?.districts?.find((item: any) => item?.id === (patientValues?.commune as any)?.districtId);

        const submitData = {
          ...values,
          ...userInfo,
          patient: {
            person: {
              ...personValues,
              ccountry: null,
              country: personValues?.ccountry?.code || "",
              gender: personValues?.gender?.name || "",
              dobDay: Number(personValues?.dobDay) || "",
              dobMonth: Number(personValues?.dobMonth) || "",
              dobYear: Number(personValues?.dobYear) || "",
              phoneNumber: personValues?.phoneNumber || null,
            },
            personName: personValues?.fullName || "",
            allergyStatus: patientValues?.allergyStatus || "",
            phoneNumber: personValues?.phoneNumber || null,
            idNumber: personValues?.idNumber || "",

            gender: personValues?.gender?.name || "",
            genderCode: personValues?.gender?.code || "",

            ethnicityId: patientValues?.ethnicity?.id || "",
            ethnicityName: patientValues?.ethnicity?.name || "",
            ethnicityCode: patientValues?.ethnicity?.code || "",

            referrerCode: patientValues?.referrer?.code || "",
            referrerName: patientValues?.referrer?.name || "",
            address: patientValues?.address || "",
            administrativeUnit: patientValues?.administrativeUnit || "",

            provinceId: patientValues?.province?.id || "",
            provinceName: patientValues?.province?.name || "",
            provinceCode: patientValues?.province?.code || "",

            districtId: districtValue?.id || "",
            districtName: districtValue?.name || "",
            districtCode: districtValue?.code || "",

            communeId: patientValues?.commune?.id || "",
            communeName: patientValues?.commune?.name || "",
            communeCode: patientValues?.commune?.code || "",

            occupationId: patientValues?.occupation?.id || "",
            occupationName: patientValues?.occupation?.name || "",
            occupationCode: patientValues?.occupation?.code || "",

            appointmentDate: formatDateDTO(patientValues?.appointmentDate || currentDate),
            deleted: patientValues?.deleted,
            deletedBy: patientValues?.deletedBy,
            deletedDate: patientValues?.deletedDate,
            deletedReason: patientValues?.deletedReason,
            id: patientValues?.id || "",
          },
          visit: {
            visitObjectTypeId: visitValues?.visitObjectType?.id || "",
            visitObjectTypeName: visitValues?.visitObjectType?.name || "",
            visitObjectTypeCode: visitValues?.visitObjectType?.code || "",
            policyCode: visitValues?.policyCode?.code || "",
            visitTypeId: visitValues?.visitType?.id || "",
            visitTypeName: visitValues?.visitType?.name || "",
            visitTypeCode: visitValues?.visitType?.code || "",

            visitReasonName: visitValues?.visitReason || "",

            termSvcId: visitValues?.service?.id || null,
            termSvcName: visitValues?.service?.name || null,
            termSvcCode: visitValues?.service?.code || null,

            deptId: visitValues?.dept?.id || null,
            deptName: visitValues?.dept?.name || null,
            deptCode: visitValues?.dept?.code || null,

            attributes: {
              ...visitValues?.attributes,
            },
            sortWeight: visitValues?.sortWeight,
          },
          orders: arr_orders,
          ...(isBHYT && {
            insurance: {
              insCode: [
                (insuranceValues?.insCodeDoiTuong ?? '').toUpperCase(),
                insuranceValues?.insCodeMucHuong,
                insuranceValues?.insCodeNoiPhatHanh,
                insuranceValues?.insCodeMaBhxh,
              ].filter((value) => Boolean(value)).join(''),
              insLevelCode: insuranceValues?.examLevel?.code || insuranceValues?.insLevelCode,
              insLevelName: insuranceValues?.examLevel?.name || insuranceValues?.insLevelName,
              insKcbbdCode: insuranceValues?.insKcbbdCode,
              insKcbbdName: insuranceValues?.insKcbbdName,
              insKcbbdProvince: insuranceValues?.insKcbbdProvince,
              insFromYear: !insuranceValues?.insFromYear ? null : Number(insuranceValues?.insFromYear),
              insFromMonth: !insuranceValues?.insFromMonth ? null : Number(insuranceValues?.insFromMonth),
              insFromDay: !insuranceValues?.insFromDay ? null : Number(insuranceValues?.insFromDay),
              insToYear: !insuranceValues?.insToYear ? null : Number(insuranceValues?.insToYear),
              insToMonth: !insuranceValues?.insToMonth ? null : Number(insuranceValues?.insToMonth),
              insToDay: !insuranceValues?.insToDay ? null : Number(insuranceValues?.insToDay),
              insNgaydu5namYear: !insuranceValues?.insNgaydu5namYear ? null : Number(insuranceValues?.insNgaydu5namYear),
              insNgaydu5namMonth: !insuranceValues?.insNgaydu5namMonth ? null : Number(insuranceValues?.insNgaydu5namMonth),
              insNgaydu5namDay: !insuranceValues?.insNgaydu5namDay ? null : Number(insuranceValues?.insNgaydu5namDay),
              insNgaymienchitraYear: !insuranceValues?.insNgaymienchitraYear ? null : Number(insuranceValues?.insNgaymienchitraYear),
              insNgaymienchitraMonth: !insuranceValues?.insNgaymienchitraMonth ? null : Number(insuranceValues?.insNgaymienchitraMonth),
              insNgaymienchitraDay: !insuranceValues?.insNgaymienchitraDay ? null : Number(insuranceValues?.insNgaymienchitraDay),
              insCoveragePercentage: !insuranceValues?.insCoveragePercentage ? null : Number(insuranceValues?.insCoveragePercentage),
              isMienCungChiTra: Number(insuranceValues?.isMienCungChiTra) || null,
              //
              ptName: personValues?.fullName,
              ptBodDay: !personValues?.dobDay ? null : Number(personValues?.dobDay),
              ptBodMonth: !personValues?.dobMonth ? null : Number(personValues?.dobMonth),
              ptBodYear: !personValues?.dobYear ? null : Number(personValues?.dobYear),
              ptGender: personValues?.gender?.name || "",
              ptGenderName: personValues?.gender?.name || "",
              insAddress: insuranceValues?.insAddress || patientValues?.administrativeUnit || "",
              id: insuranceValues?.id,
              isUpdateCard: !visitValues?.id
                ? false
                : checkInsuranceInfoChange(
                  values?.initialDetailValues?.insurance,
                  insuranceValues
                ),
              effectiveFromDay: !insuranceValues?.effectiveFromDay ? null : Number(insuranceValues?.effectiveFromDay),
              effectiveFromMonth: !insuranceValues?.effectiveFromMonth ? null : Number(insuranceValues?.effectiveFromMonth),
              effectiveFromYear: !insuranceValues?.effectiveFromYear ? null : Number(insuranceValues?.effectiveFromYear),
              effectiveToDay: !insuranceValues?.effectiveToDay ? null : Number(insuranceValues?.effectiveToDay),
              effectiveToMonth: !insuranceValues?.effectiveToMonth ? null : Number(insuranceValues?.effectiveToMonth),
              effectiveToYear: !insuranceValues?.effectiveToYear ? null : Number(insuranceValues?.effectiveToYear),
            }
          }),
          ...(values?.vitalSigns && {
            vitalSigns: values?.vitalSigns
          }),
          ...(values?.additionalForms && {
            additionalForms: {
              ...values?.additionalForms,
              ...values?.familyRecords
            }
          }),
          insLookup: toBoolean(configuration?.insLookup) ? false : (values?.insLookup || thongTinBenhNhan?.insLookup), // Nếu có cấu hình trả về thì mới check thông tuyến trên UI
        };

        let requestBodyAdd = values?.allowDisableRules ? values : {
            ...submitData,
            appointmentId: values?.appointmentId,
        };

        let requestBodyUpdate = values?.allowDisableRules ? values : {
            ...submitData,
            patient: {
                ...patientValues,
                ...submitData?.patient,
                isUpdatePerson: true,
                id: patientValues?.id
            },
            visit: {
                ...visitValues,
                ...submitData?.visit,
                id: visitValues?.id
            },
            orders: [...submitData?.orders],
            ...(isBHYT && {
                insurance: {
                    ...insuranceValues,
                    ...submitData?.insurance,
                    id: insuranceValues?.id
                }
            }),
            encounter: values?.encounter ? values?.encounter : null
        };
        
        try {
            setIsLoading(true);
            let { data } = (patientValues?.id && visitValues?.id)
                ? await updateBenhNhan(requestBodyUpdate, handleFormSubmit)
                : await addBenhNhan(requestBodyAdd, handleFormSubmit);
            if (CODE.SUCCESS === data?.code) {
                toast.success(((patientValues?.id && visitValues?.id) ? "Cập nhật" : "Thêm") + " bệnh nhân thành công");
                let dataConverted = convertDataBenhNhan(data?.data);
                let orderMain = data?.data?.orders?.find((order: any) => order?.fulfillDeptId);
                if (orderMain) {
                    setOrderMain(orderMain);
                }
                formRef?.current?.setValues(dataConverted);
                setThongTinBenhNhan({
                    ...thongTinBenhNhan,
                    isChangeThongTinBenhNhan: !thongTinBenhNhan?.isChangeThongTinBenhNhan
                });
            } else {
                let errorMesage = data?.data?.[0]?.errorMessage || "Xảy ra lỗi, vui lòng thử lại!";
                toast.warning(errorMesage);
            }
        } catch (error) {
            toast.warning("Xảy ra lỗi, vui lòng thử lại!");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleClosePrint = useCallback(() => {
        setIsPrint(false)
    }, []);

    const handleOpenDSBenhNhan = useCallback(() => {
        setOpenDSDSBenhNhan(true);
    }, []);

    const handleCloseDSBenhNhan = useCallback(() => {
        setOpenDSDSBenhNhan(false);
    }, []);

    const contextValue = useMemo(() => ({
        thongTinBenhNhan, setThongTinBenhNhan,
        isPrint, setIsPrint,
        handleAddTab,
        DSDichVu, setDSDichVu,
        isPay, setIsPay,
        orderMain, setOrderMain,
        convertDataBenhNhan,
        patientRenewToggle,
        setPatientRenewToggle
    }), [
        thongTinBenhNhan,
        isPrint,
        handleAddTab,
        DSDichVu,
        isPay,
        orderMain,
        patientRenewToggle
    ]);

    return (
        <Formik<ITiepDon>
            initialValues={initialValuesTiepDon}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
            innerRef={formRef}
        >
            <PhanHeTiepNhanContext.Provider value={contextValue}>
                <div className="reception-list spaces mt-6">
                    <div className="reception__header spaces pt-3 pb-6 border-bottom">
                        <Button
                            className="btn-blue-outlined-custom ms-4 spaces h-29"
                            onClick={handleOpenDSBenhNhan}
                        >
                            <i className="bi bi-people icon-call color-blue-custom fs-4"></i>
                            <span>Danh sách BN</span>
                        </Button>
                    </div>
                    <TiepNhan
                        openDSBenhNhan={openDSBenhNhan}
                        handleCloseDSBenhNhan={handleCloseDSBenhNhan}
                    />
                </div>
                {isPrint && (
                    <ComponentPrint
                        open={isPrint}
                        size="sm"
                        title="Phiếu tiếp đón"
                        handleClose={handleClosePrint}
                        data={convertDataTiepNhan({...thongTinBenhNhan, DSDichVu: DSDichVu})}
                        template={phieuTiepNhan}
                    />
                )}
            </PhanHeTiepNhanContext.Provider>
        </Formik>
    )
}

export default PhanHeTiepNhan;
